import { type ReactNode } from 'react';
import type { TierDraft } from '../utils/group-tier-helpers';

const BASE =
  'w-16 rounded-md border bg-surface px-2 py-1 text-xs text-text-primary tabular-nums transition-colors focus:border-brand-red focus:outline-none focus:ring-1 focus:ring-brand-red/30';
const OK = 'border-border-subtle';
const ERR = 'border-error/50';
const LOCK = 'opacity-50 cursor-not-allowed';

function cls(err: boolean, extra?: string): string {
  return `${BASE} ${err ? ERR : OK} ${extra ?? ''}`;
}

type Props = {
  tier: TierDraft;
  isFirst: boolean;
  isLast: boolean;
  rangeError: boolean;
  amountError: boolean;
  onChange: (t: TierDraft) => void;
};

function MinInput(p: Props): ReactNode {
  const locked = !p.isFirst;
  return (
    <input
      type="number"
      value={p.tier.min_avg}
      onChange={(e) => p.onChange({ ...p.tier, min_avg: +e.target.value })}
      disabled={locked}
      className={cls(p.rangeError, locked ? LOCK : '')}
    />
  );
}

function MaxInput(p: Props): ReactNode {
  if (p.isLast)
    return (
      <span className="w-16 text-center text-xs text-text-tertiary">∞</span>
    );
  return (
    <input
      type="number"
      value={p.tier.max_avg ?? ''}
      onChange={(e) => {
        const n = e.target.value === '' ? null : +e.target.value;
        p.onChange({ ...p.tier, max_avg: n });
      }}
      className={cls(p.rangeError)}
    />
  );
}

function Range(p: Props): ReactNode {
  return (
    <div className="flex items-center gap-1.5">
      <MinInput {...p} />
      <span className="text-[10px] text-text-tertiary">—</span>
      <MaxInput {...p} />
    </div>
  );
}

function Amount(p: Props): ReactNode {
  return (
    <div className="flex items-center gap-1">
      <input
        type="number"
        value={p.tier.amount}
        onChange={(e) => p.onChange({ ...p.tier, amount: +e.target.value })}
        className={cls(p.amountError, 'w-20')}
      />
      <span className="text-[10px] text-text-tertiary">$</span>
    </div>
  );
}

export function GroupTierInputs(p: Props): ReactNode {
  return (
    <>
      <Range {...p} />
      <Amount {...p} />
    </>
  );
}
